import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import './Auth.css';
const API = 'https://cybercomply-ai.onrender.com';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authKey, setAuthKey] = useState(0);
  const login  = (userData) => { setUser(userData); setAuthKey(k => k + 1); };
  const logout = () => setUser(null);
  return (
    <AuthContext.Provider value={{ user, login, logout, authKey }}>
      {children}
    </AuthContext.Provider>
  );
}

function getStrength(pw) {
  const checks = {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    number:    /[0-9]/.test(pw),
    special:   /[^A-Za-z0-9]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', '#e74c3c', '#f39c12', '#3498db', '#00d4aa'];
  return { score, label: labels[score], color: colors[score], checks };
}

function YubiKeyDevice({ onPress, verified }) {
  const [pressing, setPressing] = useState(false);
  const handlePress = () => {
    if (verified) return;
    setPressing(true);
    setTimeout(() => { setPressing(false); onPress(); }, 600);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '10px 0' }}>
      <div style={{
        width: '180px', height: '56px', borderRadius: '28px',
        background: verified ? '#1a3a2a' : '#1a1a2e',
        border: `2px solid ${verified ? '#00d4aa' : '#444'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', transition: 'all 0.3s'
      }}>
        <div style={{ width: '28px', height: '20px', background: '#888', borderRadius: '3px', border: '1px solid #666' }} />
        <div onClick={handlePress} style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: verified ? '#00d4aa' : pressing ? '#c9a227' : '#ffd700',
          cursor: verified ? 'default' : 'pointer',
          boxShadow: `0 0 12px ${verified ? '#00d4aa66' : '#ffd70066'}`,
          transform: pressing ? 'scale(0.9)' : 'scale(1)',
          transition: 'all 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', fontWeight: 'bold', color: '#000'
        }}>
          {verified ? '✓' : ''}
        </div>
        <div style={{ fontSize: '9px', color: '#888', letterSpacing: '1px' }}>YUBICO</div>
      </div>
      <div style={{ fontSize: '11px', color: verified ? '#00d4aa' : '#888', marginTop: '6px' }}>
        {verified ? 'Authentication successful ✓' : 'Press the gold button to authenticate'}
      </div>
    </div>
  );
}

export default function Auth() {
  const { login } = useAuth();
  const errorRef = useRef(null);

  const [mode, setMode]                   = useState('login');
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [name, setName]                   = useState('');
  const [company, setCompany]             = useState('');
  const [confirmPw, setConfirmPw]         = useState('');
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');
  const [loading, setLoading]             = useState(false);
  const [yubiEnabled, setYubiEnabled]     = useState(false);
  const [yubiOtp, setYubiOtp]             = useState('');
  const [yubiVerified, setYubiVerified]   = useState(false);
  const [yubiVerifying, setYubiVerifying] = useState(false);

  useEffect(() => {
    setEmail(''); setPassword(''); setName(''); setCompany(''); setConfirmPw('');
    setError(''); setSuccess('');
    setYubiEnabled(false); setYubiOtp(''); setYubiVerified(false); setYubiVerifying(false);
  }, [mode]);

  // Scroll error into view whenever it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const showError = (msg) => {
    setSuccess('');
    setError(msg);
  };

  const strength = getStrength(password);

  const verifyYubiOtp = () => {
    if (yubiOtp.length < 6) return;
    setYubiVerifying(true);
    setTimeout(() => { setYubiVerifying(false); setYubiVerified(true); }, 1500);
  };

  const handleLogin = async () => {
    setError(''); setSuccess('');
    if (!email || !password) { showError('Please enter your email and password.'); return; }
    if (yubiEnabled && !yubiVerified) { showError('Please verify your YubiKey first.'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        login({ name: data.user?.name || data.name || email.split('@')[0], email, company: data.user?.company || '' });
      } else {
        showError(data.error || 'Login failed. Please check your credentials.');
      }
    } catch { showError('Cannot connect to server. Please try again shortly.'); }
    setLoading(false);
  };

  const handleRegister = async () => {
    setError(''); setSuccess('');
    if (!name || !email || !password || !confirmPw) { showError('Please fill in all fields.'); return; }
    if (password !== confirmPw) { showError('Passwords do not match.'); return; }
    if (strength.score < 2) { showError('Password too weak — add uppercase letters, numbers or special characters.'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, company }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setError('');
        setSuccess('Account created! Check your email for a welcome message, then sign in.');
        setTimeout(() => setMode('login'), 3000);
      } else {
        // Show the exact error from the backend — including "already exists"
        const msg = data.error || 'Registration failed. Please try again.';
        if (msg.toLowerCase().includes('already exists')) {
          showError('An account with this email address already exists. Please sign in instead.');
        } else {
          showError(msg);
        }
      }
    } catch { showError('Cannot connect to server. Please try again shortly.'); }
    setLoading(false);
  };

  const handleForgot = async () => {
    setError(''); setSuccess('');
    if (!email) { showError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess('Password reset link sent! Check your inbox.'); }
      else { showError(data.error || 'Failed to send reset email.'); }
    } catch { showError('Cannot connect to server. Please try again shortly.'); }
    setLoading(false);
  };

  return (
    <div className="auth-bg">
      <div className="cyber-grid" />
      <div className="cyber-lines">
        {[0,1,2,3,4].map(i => (
          <div key={i} className="cyber-line" style={{ '--i': i }} />
        ))}
      </div>

      <div className="auth-card">

        <div className="auth-logo">
          <div className="auth-logo-icon">🛡</div>
          <div className="auth-logo-text">Cyber<span>Comply</span> AI</div>
        </div>

        {mode !== 'forgot' && (
          <div style={{
            display: 'flex', background: 'rgba(255,255,255,0.05)',
            borderRadius: '8px', padding: '4px', marginBottom: '24px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            {[['login','Sign in'],['register','Create account']].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)} style={{
                flex: 1, padding: '9px 8px', border: 'none', borderRadius: '6px',
                cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                transition: 'all 0.2s',
                background: mode === m ? 'rgba(0,212,170,0.15)' : 'transparent',
                color: mode === m ? '#00d4aa' : 'rgba(255,255,255,0.45)',
                borderBottom: mode === m ? '2px solid #00d4aa' : '2px solid transparent',
              }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {mode === 'forgot' && (
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '36px' }}>🔐</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginTop: '8px' }}>
              Reset your password
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginTop: '4px' }}>
              Enter your email and we'll send a reset link
            </div>
          </div>
        )}

        {/* Error — ref attached so it scrolls into view */}
        {error && (
          <div ref={errorRef} className="auth-error">
            ⚠ {error}
          </div>
        )}
        {success && <div className="auth-success">✅ {success}</div>}

        {/* ── LOGIN ── */}
        {mode === 'login' && (
          <>
            <div className="auth-field">
              <label>Email address</label>
              <input type="email" placeholder="you@company.com"
                autoComplete="new-password" value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            </div>
            <div className="auth-field">
              <label>Password</label>
              <input type="password" placeholder="Your password"
                autoComplete="new-password" value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            </div>

            <div className="yubikey-toggle"
              onClick={() => { setYubiEnabled(!yubiEnabled); setYubiVerified(false); setYubiOtp(''); }}>
              <div className={`yubikey-check ${yubiEnabled ? 'checked' : ''}`}>
                {yubiEnabled && '✓'}
              </div>
              <span>🔑 Use YubiKey hardware authentication</span>
              <span className="yubikey-badge">Recommended</span>
            </div>

            {yubiEnabled && (
              <div className="yubikey-field">
                <YubiKeyDevice
                  onPress={() => { setYubiVerifying(true); setTimeout(() => { setYubiVerifying(false); setYubiVerified(true); }, 600); }}
                  verified={yubiVerified}
                />
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '11px', margin: '8px 0' }}>
                  — or enter OTP manually —
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" className="yubikey-input" placeholder="Paste YubiKey OTP..."
                    autoComplete="off" value={yubiOtp}
                    onChange={e => setYubiOtp(e.target.value)}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid rgba(0,212,170,0.3)', background: 'rgba(0,212,170,0.05)',
                      color: '#fff', fontSize: '13px', outline: 'none' }} />
                  <button onClick={verifyYubiOtp} disabled={yubiVerified} style={{
                    padding: '8px 14px', borderRadius: '6px', border: 'none',
                    background: yubiVerified ? '#00d4aa' : '#ffd700',
                    color: '#000', fontWeight: 700,
                    cursor: yubiVerified ? 'default' : 'pointer', fontSize: '13px'
                  }}>
                    {yubiVerifying ? '...' : yubiVerified ? '✓' : 'Verify'}
                  </button>
                </div>
                {yubiVerifying && (
                  <div className="yubikey-hint" style={{ textAlign: 'center', marginTop: '6px' }}>
                    Verifying OTP with Yubico servers...
                  </div>
                )}
              </div>
            )}

            <button className="auth-btn" onClick={handleLogin}
              disabled={loading || (yubiEnabled && !yubiVerified)}>
              {loading ? 'Signing in...' : yubiEnabled && !yubiVerified ? '🔑 Verify YubiKey first' : 'Sign in →'}
            </button>

            <div className="auth-links">
              <span onClick={() => setMode('forgot')}>Forgot password?</span>
            </div>
          </>
        )}

        {/* ── REGISTER ── */}
        {mode === 'register' && (
          <>
            <div className="auth-field">
              <label>Full name</label>
              <input type="text" placeholder="Your full name"
                autoComplete="new-password" value={name}
                onChange={e => setName(e.target.value)} />
            </div>
            <div className="auth-field">
              <label>Company name</label>
              <input type="text" placeholder="e.g. Leicester Tech Solutions Ltd"
                autoComplete="new-password" value={company}
                onChange={e => setCompany(e.target.value)} />
            </div>
            <div className="auth-field">
              <label>Email address</label>
              <input type="email" placeholder="you@company.com"
                autoComplete="new-password" value={email}
                onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="auth-field">
              <label>Password</label>
              <input type="password" placeholder="Create a strong password"
                autoComplete="new-password" value={password}
                onChange={e => setPassword(e.target.value)} />
              {password.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{
                        flex: 1, height: '4px', borderRadius: '2px',
                        background: i <= strength.score ? strength.color : 'rgba(255,255,255,0.1)',
                        transition: 'background 0.3s'
                      }} />
                    ))}
                  </div>
                  <div style={{ fontSize: '12px', color: strength.color, fontWeight: 600, marginBottom: '8px' }}>
                    {strength.label}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                    {[
                      { key: 'length',    label: '8+ characters' },
                      { key: 'uppercase', label: 'Uppercase letter' },
                      { key: 'number',    label: 'Number (0–9)' },
                      { key: 'special',   label: 'Special character' },
                    ].map(({ key, label }) => (
                      <div key={key} style={{
                        fontSize: '11px',
                        color: strength.checks[key] ? '#00d4aa' : 'rgba(255,255,255,0.35)',
                        display: 'flex', alignItems: 'center', gap: '5px'
                      }}>
                        <span>{strength.checks[key] ? '✓' : '○'}</span> {label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="auth-field">
              <label>Confirm password</label>
              <input type="password" placeholder="Repeat your password"
                autoComplete="new-password" value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)} />
              {confirmPw.length > 0 && (
                <div style={{ fontSize: '12px', marginTop: '5px',
                  color: confirmPw === password ? '#00d4aa' : '#e74c3c' }}>
                  {confirmPw === password ? '✓ Passwords match' : '✗ Passwords do not match'}
                </div>
              )}
            </div>
            <button className="auth-btn" onClick={handleRegister} disabled={loading}>
              {loading ? 'Creating account...' : 'Create account →'}
            </button>
            <div className="auth-links">
              <span onClick={() => setMode('login')}>← Already have an account? Sign in</span>
            </div>
          </>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {mode === 'forgot' && (
          <>
            <div className="auth-field">
              <label>Email address</label>
              <input type="email" placeholder="you@company.com"
                autoComplete="new-password" value={email}
                onChange={e => setEmail(e.target.value)} />
            </div>
            <button className="auth-btn" onClick={handleForgot} disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link →'}
            </button>
            <div className="auth-links">
              <span onClick={() => setMode('login')}>← Back to sign in</span>
            </div>
          </>
        )}

        <div className="auth-footer">
          🔒 256-bit encrypted · IASME Assured · GDPR compliant
        </div>
      </div>
    </div>
  );
}